import React, { useEffect, useRef, useState } from 'react';
import { Button, Space } from 'antd';
import { 
  ZoomInOutlined, 
  ZoomOutOutlined, 
  FullscreenOutlined,
  ReloadOutlined 
} from '@ant-design/icons';
import mermaid from 'mermaid';

// Initialize Mermaid
mermaid.initialize({
  startOnLoad: false,
  theme: 'default',
  securityLevel: 'loose',
  flowchart: {
    useMaxWidth: false,
    htmlLabels: true,
    curve: 'basis'
  }
});

const MermaidViewer = ({ code, height = 600 }) => {
  const containerRef = useRef(null);
  const wrapperRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [renderError, setRenderError] = useState(null);

  // Render mermaid diagram
  useEffect(() => {
    const renderDiagram = async () => {
      if (!code || !containerRef.current) return;

      try {
        setRenderError(null);
        const id = `mermaid-${Date.now()}`;
        const { svg } = await mermaid.render(id, code);
        
        if (containerRef.current) {
          containerRef.current.innerHTML = svg;
        }
      } catch (error) {
        console.error('Mermaid render error:', error);
        setRenderError(error.message);
      }
    };

    renderDiagram();
  }, [code]);

  // Apply transform
  useEffect(() => {
    if (wrapperRef.current) {
      wrapperRef.current.style.transform = `translate(${position.x}px, ${position.y}px) scale(${scale})`;
    }
  }, [scale, position]);

  // Zoom functions
  const zoomIn = () => {
    setScale(prev => Math.min(prev + 0.2, 5));
  };

  const zoomOut = () => {
    setScale(prev => Math.max(prev - 0.2, 0.1));
  };

  const resetView = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  const fitToScreen = () => {
    if (!containerRef.current || !wrapperRef.current) return;

    const container = containerRef.current.parentElement;
    const wrapper = wrapperRef.current;
    
    const containerRect = container.getBoundingClientRect();
    const wrapperRect = wrapper.getBoundingClientRect();
    
    const scaleX = containerRect.width / wrapperRect.width;
    const scaleY = containerRect.height / wrapperRect.height;
    
    const newScale = Math.min(scaleX, scaleY, 1) * 0.9;
    setScale(newScale);
    setPosition({ x: 0, y: 0 });
  };

  // Mouse events for panning
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Wheel zoom
  const handleWheel = (e) => {
    e.preventDefault();
    
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    const newScale = Math.max(0.1, Math.min(5, scale + delta));
    
    // Zoom towards mouse position
    const container = e.currentTarget;
    const rect = container.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const xs = (x - position.x) / scale;
    const ys = (y - position.y) / scale;
    
    setScale(newScale);
    setPosition({
      x: x - xs * newScale,
      y: y - ys * newScale
    });
  };

  return (
    <div style={{ position: 'relative' }}>
      {/* Zoom controls */}
      <div style={{
        position: 'absolute',
        top: 10,
        right: 10,
        zIndex: 1000,
        background: 'rgba(255, 255, 255, 0.95)',
        padding: '10px',
        borderRadius: '8px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
      }}>
        <Space direction="vertical" size="small">
          <Button icon={<ZoomInOutlined />} onClick={zoomIn} size="small">
            Zoom In
          </Button>
          <Button icon={<ZoomOutOutlined />} onClick={zoomOut} size="small">
            Zoom Out
          </Button>
          <Button icon={<FullscreenOutlined />} onClick={fitToScreen} size="small">
            Fit
          </Button>
          <Button icon={<ReloadOutlined />} onClick={resetView} size="small">
            Reset
          </Button>
          <div style={{ 
            textAlign: 'center', 
            fontSize: '12px', 
            fontWeight: 'bold',
            padding: '4px 0'
          }}>
            {Math.round(scale * 100)}%
          </div>
        </Space>
      </div>

      {/* Diagram container */}
      <div
        style={{
          width: '100%',
          height: `${height}px`,
          overflow: 'hidden',
          border: '2px solid #ddd',
          borderRadius: '8px',
          background: '#ffffff',
          cursor: isDragging ? 'grabbing' : 'grab',
          position: 'relative'
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
      >
        <div
          ref={wrapperRef}
          style={{
            transformOrigin: '0 0',
            transition: isDragging ? 'none' : 'transform 0.1s ease-out',
            display: 'inline-block',
            minWidth: '100%',
            minHeight: '100%',
            padding: '40px'
          }}
        >
          {renderError ? (
            <div style={{ 
              padding: '40px', 
              color: '#ff4d4f',
              fontSize: '14px'
            }}>
              Error rendering diagram: {renderError}
            </div>
          ) : (
            <div ref={containerRef} />
          )}
        </div>
      </div>
    </div>
  );
};

export default MermaidViewer;
